import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * useResizableColumns
 *
 * Provides drag-to-resize column widths for a table, persisted to localStorage.
 *
 * @param {string} storageKey  - Unique key for localStorage (e.g. "testcases-cols")
 * @param {Array}  defaultCols - Array of { key, label, defaultWidth }
 * @returns {{ colWidths, thProps, tableRef }}
 */
export function useResizableColumns(storageKey, defaultCols) {
  // Load persisted widths or fall back to defaults
  const loadWidths = () => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Merge with defaults so new columns added later still get a default
        const merged = {};
        defaultCols.forEach(c => {
          merged[c.key] = parsed[c.key] || c.defaultWidth;
        });
        return merged;
      }
    } catch (_) { /* ignore parse errors */ }
    const initial = {};
    defaultCols.forEach(c => { initial[c.key] = c.defaultWidth; });
    return initial;
  };

  const [colWidths, setColWidths] = useState(loadWidths);
  const tableRef = useRef(null);
  const dragging = useRef(null); // { key, startX, startWidth }

  const persist = useCallback((widths) => {
    try { localStorage.setItem(storageKey, JSON.stringify(widths)); } catch (_) { /* quota exceeded, ignore */ }
  }, [storageKey]);

  const onMouseDown = useCallback((key, e) => {
    e.preventDefault();
    dragging.current = { key, startX: e.clientX, startWidth: colWidths[key] };

    const onMouseMove = (moveEvent) => {
      if (!dragging.current) return;
      const delta = moveEvent.clientX - dragging.current.startX;
      const newWidth = Math.max(50, dragging.current.startWidth + delta);
      setColWidths(prev => {
        const next = { ...prev, [dragging.current.key]: newWidth };
        persist(next);
        return next;
      });
    };

    const onMouseUp = () => {
      dragging.current = null;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, [colWidths, persist]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, []);

  /**
   * Returns props to spread onto a <th> element.
   * Usage: <th {...thProps('summary')}>Summary</th>
   */
  const thProps = useCallback((key) => ({
    style: {
      width: colWidths[key],
      minWidth: colWidths[key],
      maxWidth: colWidths[key],
      position: 'relative',
      userSelect: 'none',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    className: 'resizable-th',
    children: undefined, // caller provides children
  }), [colWidths]);

  return { colWidths, thProps, onMouseDown, tableRef };
}
